import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import logService, { ActionCategory } from '../services/logging/logService';

/**
 * Hook semplificato per il tracciamento delle azioni utente
 * Fornisce metodi per registrare diverse tipologie di interazioni
 */
export const useTracking = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const sessionRef = useRef<{ startTime: number; lastPageLoaded: string | null }>({ 
    startTime: Date.now(), 
    lastPageLoaded: null 
  });

  // Metodi di tracking semplificati e ottimizzati
  return {
    /**
     * Traccia clic su elementi UI
     */
    trackClick: (
      elementName: string,
      elementId?: string,
      metadata?: Record<string, any>
    ) => {
      logService.logAction(
        ActionCategory.USER_ACTION,
        'click',
        `Clicked on ${elementName}`,
        {
          ...metadata,
          elementName,
          elementId,
          path: currentPath,
          logType: 'api' // Categorize as API interaction
        }
      );
    },

    /**
     * Traccia invio di form con filtro automatico dati sensibili
     */
    trackFormSubmit: (
      formName: string,
      formData?: Record<string, any>,
      metadata?: Record<string, any>
    ) => {
      // Filtra dati sensibili
      const safeFormData = formData 
        ? Object.entries(formData).reduce((acc, [key, value]) => {
            if (key.toLowerCase().includes('password') || 
                key.toLowerCase().includes('token') ||
                key.toLowerCase().includes('secret')) {
              acc[key] = '[FILTERED]';
            } else {
              acc[key] = value;
            }
            return acc;
          }, {} as Record<string, any>) 
        : undefined;

      logService.logAction(
        ActionCategory.USER_ACTION,
        'form_submit',
        `Submitted form: ${formName}`,
        {
          ...metadata,
          formName,
          formData: safeFormData,
          path: currentPath,
          logType: 'api' // Categorize as API interaction
        }
      );
    },

    /**
     * Traccia ricerche effettuate dall'utente
     */
    trackSearch: (
      searchQuery: string,
      resultCount?: number,
      metadata?: Record<string, any>
    ) => {
      // Aggiungiamo un identificatore univoco a ogni chiamata di ricerca
      const uniqueId = Date.now() + Math.random().toString(36).substring(2, 9);
      
      logService.logAction(
        ActionCategory.SEARCH,
        'search',
        `Search query: ${searchQuery}`,
        {
          ...metadata,
          searchQuery,
          resultCount,
          path: currentPath,
          uniqueId: uniqueId, // Identificatore univoco per evitare debounce non desiderato
          logType: 'api' // Aggiungiamo questa categoria per il tracciamento
        }
      );
    },

    /**
     * Traccia applicazione filtri
     */
    trackFilter: (
      filterName: string,
      filterValue: any,
      metadata?: Record<string, any>
    ) => {
      logService.logAction(
        ActionCategory.FILTER,
        'apply_filter',
        `Applied filter: ${filterName}`,
        {
          ...metadata,
          filterName,
          filterValue,
          path: currentPath,
          logType: 'api' // Aggiungiamo questa categoria per il tracciamento
        }
      );
    },

    /**
     * Traccia errori
     */
    trackError: (
      action: string,
      error: Error | string,
      metadata?: Record<string, any>
    ) => {
      logService.logError(
        action,
        error,
        {
          ...metadata,
          path: currentPath,
        }
      );
    },

    /**
     * Traccia caricamento pagina
     * Da usare all'interno del componente della pagina
     */
    trackPageLoad: (pageName: string, metadata?: Record<string, any>) => {
      // Evita duplicazione se la pagina è già stata tracciata
      if (sessionRef.current.lastPageLoaded !== currentPath) {
        logService.logNavigation(
          currentPath,
          `Page loaded: ${pageName}`,
          { 
            ...metadata,
            pageName,
            sessionTime: Math.floor((Date.now() - sessionRef.current.startTime) / 1000),
            logType: 'session' // Categorize as session tracking
          }
        );
        sessionRef.current.lastPageLoaded = currentPath;
      }
    },

    // Esporta logService per usi avanzati
    logService,
  };
};

export default useTracking;
